import { API_BASE_URL } from '../../api/constants.js';

/**
 * RecoveryApi — dedicated Symfony API client for the password-recovery flow.
 * Deliberately does NOT extend the shared `BaseApi`/`apiRequest` (`api/utils.js`):
 * that client hard-navigates to `/authorization/login/` on any 401
 * (`api/authSession.js`). Every caller here is anonymous by definition — the
 * visitor is recovering a password precisely because they cannot sign in — and
 * an expired or already-used reset link is a normal inline state, not a reason
 * to throw the visitor off the page.
 *
 * Both routes are PUBLIC_ACCESS on the Symfony side; no credential is ever sent.
 */
async function request(path, { method = 'GET', body } = {}) {
	const response = await fetch(`${API_BASE_URL}${path}`, {
		method,
		credentials: 'include',
		headers: { 'Content-Type': 'application/json' },
		...(body !== undefined && { body: JSON.stringify(body) })
	});

	let data = null;
	try {
		data = await response.json();
	} catch {
		data = null;
	}

	if (!response.ok) {
		const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
		error.response = { data, status: response.status };
		throw error;
	}

	return { data };
}

class RecoveryApi {
	/**
	 * POST /api/v1/auth/password/reset-request — always 200 with the same body,
	 * whether or not the address is registered. The answer is deliberately
	 * uninformative, so the page must not present it as "we found you". 429
	 * past 3 requests/hour/IP.
	 */
	static requestReset(email) {
		return request('/auth/password/reset-request', { method: 'POST', body: { email } });
	}

	/**
	 * POST /api/v1/auth/password/reset — 200 on success. 400 for a token that
	 * is unknown, expired, already spent, or not a recovery token (all four
	 * collapse to one answer by design). 422 for a weak or mismatched
	 * password. 429 past 5 attempts/15min/IP.
	 */
	static resetPassword({ token, password, passwordConfirmation }) {
		return request('/auth/password/reset', {
			method: 'POST',
			body: { token, password, passwordConfirmation }
		});
	}
}

export default RecoveryApi;
